import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_VEHICLE_REPORT_GRID
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
};

const toInt = (value, fallback = 0) => {
  const n = parseInt(String(value), 10);
  return Number.isFinite(n) ? n : fallback;
};

export const computeVehicleReportDerived = (fields) => {
  const previousOdometerKm = toNumber(fields.previousOdometerKm);
  const currentOdometerKm = toNumber(fields.currentOdometerKm);
  const kmMoved = Math.max(0, currentOdometerKm - previousOdometerKm);
  const fuelUsedLiters = toNumber(fields.fuelUsedLiters);
  const fuelCostCard = toNumber(fields.fuelCostCard);
  const fuelCostCash = toNumber(fields.fuelCostCash);
  const totalFuelCost = fuelCostCard + fuelCostCash;
  let fuelKmPerLiter = 0;
  let literPer100Km = 0;
  if (fuelUsedLiters > 0 && kmMoved > 0) {
    fuelKmPerLiter = kmMoved / fuelUsedLiters;
    literPer100Km = (fuelUsedLiters / kmMoved) * 100;
  }
  return { kmMoved, totalFuelCost, fuelKmPerLiter, literPer100Km };
};

export const formatVehicleReportGridRow = (doc) => {
  const base = {
    id: doc.$id ?? doc.id,
    project: doc.project ?? '',
    userName: doc.userName ?? '',
    regNumber: doc.regNumber ?? '',
    rentalCompany: doc.rentalCompany ?? '',
    previousOdometerKm: toNumber(doc.previousOdometerKm),
    currentOdometerKm: toNumber(doc.currentOdometerKm),
    fuelUsedLiters: toNumber(doc.fuelUsedLiters),
    fuelCostCard: toNumber(doc.fuelCostCard),
    fuelCostCash: toNumber(doc.fuelCostCash),
    rentalDays: toInt(doc.rentalDays, 0),
    pricePerMonth: toNumber(doc.pricePerMonth),
    month: toInt(doc.month, 1),
    year: toInt(doc.year, new Date().getFullYear())
  };
  const derived = computeVehicleReportDerived(base);
  return { ...base, kmMoved: derived.kmMoved, ...derived };
};

const toPayload = (fields) => {
  const derived = computeVehicleReportDerived(fields);
  return {
    project: String(fields.project ?? '').trim(),
    userName: String(fields.userName ?? '').trim(),
    regNumber: String(fields.regNumber ?? '').trim(),
    rentalCompany: String(fields.rentalCompany ?? '').trim(),
    previousOdometerKm: toNumber(fields.previousOdometerKm),
    currentOdometerKm: toNumber(fields.currentOdometerKm),
    kmMoved: derived.kmMoved,
    fuelUsedLiters: toNumber(fields.fuelUsedLiters),
    fuelCostCard: toNumber(fields.fuelCostCard),
    fuelCostCash: toNumber(fields.fuelCostCash),
    totalFuelCost: derived.totalFuelCost,
    rentalDays: Math.min(31, Math.max(0, toInt(fields.rentalDays, 0))),
    pricePerMonth: toNumber(fields.pricePerMonth),
    fuelKmPerLiter: derived.fuelKmPerLiter,
    literPer100Km: derived.literPer100Km,
    month: toInt(fields.month, 1),
    year: toInt(fields.year, new Date().getFullYear())
  };
};

export const getVehicleReportGridErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

export const fetchAllVehicleReportGridRows = async () => {
  const all = [];
  let cursor = null;
  const limit = 500;

  for (let page = 0; page < 20; page += 1) {
    const queries = [Query.limit(limit)];
    if (cursor) queries.push(Query.cursorAfter(cursor));

    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_VEHICLE_REPORT_GRID,
      queries
    );
    all.push(...response.documents.map(formatVehicleReportGridRow));
    if (response.documents.length < limit) break;
    cursor = response.documents[response.documents.length - 1].$id;
  }

  all.sort((a, b) => {
    const y = (a.year || 0) - (b.year || 0);
    if (y !== 0) return y;
    const mo = (a.month || 0) - (b.month || 0);
    if (mo !== 0) return mo;
    const p = String(a.project || '').localeCompare(String(b.project || ''), undefined, {
      sensitivity: 'base'
    });
    if (p !== 0) return p;
    return String(a.regNumber || '').localeCompare(String(b.regNumber || ''), undefined, {
      sensitivity: 'base'
    });
  });

  return all;
};

const SESSION_CACHE_KEY = 'striitsmart:vehicleReportGrid:v1';
const SESSION_CACHE_TTL_MS = 10 * 60 * 1000;

export const readVehicleReportGridSessionCache = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rows || !Array.isArray(parsed.rows)) return null;
    if (Date.now() - (parsed.at || 0) > SESSION_CACHE_TTL_MS) return null;
    return parsed.rows.map((row) => formatVehicleReportGridRow(row));
  } catch {
    return null;
  }
};

export const writeVehicleReportGridSessionCache = (rows) => {
  try {
    sessionStorage.setItem(
      SESSION_CACHE_KEY,
      JSON.stringify({ at: Date.now(), rows: rows || [] })
    );
  } catch {
    /* quota / private mode */
  }
};

export const createVehicleReportGridRow = async (fields) => {
  const doc = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_VEHICLE_REPORT_GRID,
    ID.unique(),
    toPayload(fields)
  );
  return formatVehicleReportGridRow(doc);
};

export const upsertVehicleReportGridRow = async (id, fields) => {
  if (!id) return null;
  const doc = await databases.updateDocument(
    DATABASE_ID,
    COLLECTION_ID_VEHICLE_REPORT_GRID,
    id,
    toPayload(fields)
  );
  return formatVehicleReportGridRow(doc);
};

export const deleteVehicleReportGridRow = async (id) => {
  if (!id) return;
  await databases.deleteDocument(DATABASE_ID, COLLECTION_ID_VEHICLE_REPORT_GRID, id);
};
